import React, { useState, useEffect } from 'react';
import { Clock, Thermometer, AlertCircle, CheckCircle2 } from 'lucide-react';
import { FermentationProcess } from '../../types/baking';
import { FermentationTimeSlider } from './FermentationTimeSlider';

interface FermentationOptionProps {
  process: FermentationProcess;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  customTime: number | undefined;
  onCustomTimeChange?: (value: number) => void;
  targetBakeTime?: string;
}

const formatDateTime = (date: Date) => {
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export function FermentationOption({ 
  process, 
  selected, 
  onSelect, 
  disabled,
  customTime,
  onCustomTimeChange,
  targetBakeTime
}: FermentationOptionProps) {
  const [localCustomTime, setLocalCustomTime] = useState<number>(customTime || 24);

  useEffect(() => {
    if (customTime !== undefined) {
      setLocalCustomTime(customTime);
    }
  }, [customTime]);

  const handleTimeChange = (value: number) => {
    setLocalCustomTime(value);
    if (onCustomTimeChange) {
      onCustomTimeChange(value);
    }
  };

  const getSessionTimes = () => {
    if (!targetBakeTime || (process.id !== 'direct' && process.id !== 'slow-room')) return null;
    
    const bakeTime = new Date(targetBakeTime);
    const totalTime = process.totalTime;
    const proofTime = process.proofTime;
    const bulkTime = process.bulkTime;
    
    const shapingTime = new Date(bakeTime);
    shapingTime.setHours(shapingTime.getHours() - proofTime);
    
    const kneadingTime = new Date(bakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - totalTime);
    
    return {
      kneading: kneadingTime,
      shaping: shapingTime,
      baking: bakeTime
    };
  };

  const times = getSessionTimes();

  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`w-full text-left p-4 rounded-lg border transition-all duration-200 relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
        selected 
          ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/10 to-amber-500/5 text-white' 
          : 'border-white/10 bg-white/5 hover:bg-white/10 text-white'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-medium text-white">{process.name}</h3>
          <p className="text-sm text-white/70 mt-1">{process.description}</p>
        </div>
        {selected && (
          <CheckCircle2 className="w-5 h-5 text-amber-300" />
        )}
      </div>

      <div className="flex gap-4 mt-3 text-sm text-white/70">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{process.totalTime}h total</span>
        </div>
        <div className="flex items-center gap-1">
          <Thermometer className="w-4 h-4" />
          <span>{process.temperature === 'room' ? 'Room temp' : 'Cold'}</span>
        </div>
      </div>
      
      <div className="mt-2 text-sm text-amber-300 font-medium">
        {process.starterPercentage} starter needed
      </div>
      {selected && targetBakeTime && times && (process.id === 'direct' || process.id === 'slow-room') && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <h4 className="text-sm font-medium text-white/90 flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-400" />
            Estimated Timeline
          </h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Kneading:</span>
              <span className="text-white">{formatDateTime(times.kneading)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Shaping:</span>
              <span className="text-white">{formatDateTime(times.shaping)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Baking:</span>
              <span className="text-white">{formatDateTime(times.baking)}</span>
            </div>
          </div>
        </div>
      )}

      {selected && (process.id === 'cold-bulk' || process.id === 'pre-proofed') && customTime && onCustomTimeChange && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <FermentationTimeSlider
            label={process.id === 'cold-bulk' ? "Cold Bulk Duration" : "Cold Proof Duration"}
            value={localCustomTime}
            onChange={handleTimeChange}
            min={8}
            max={120}
            recommendedRange={process.id === 'cold-bulk' 
              ? "16-24 hours for optimal flavor development"
              : "12-48 hours for best results"
            }
            targetBakeTime={targetBakeTime}
            processType={process.id === 'cold-bulk' ? 'cold-bulk' : 'pre-proofed'}
          />
        </div>
      )}

      {disabled && (
        <div className="mt-3 flex items-start gap-2 p-3 bg-red-500/10 backdrop-blur-sm rounded-lg border border-red-500/20">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-300" />
          <p className="text-sm text-red-200">
            Not available for short fermentation (less than 18h)
          </p>
        </div>
      )}
    </button>
  );
}